/**
 * The workspace summon bar's state, owned by the workspace layer root for
 * both views (Part 06 Task 2): the per-window reset on a project, Space or
 * mode change, web summons, the double-Control shortcut and the selection
 * quote. Desktop shortcuts keep their single screen-space owner in
 * MasterLayout (`CompactCursorInputOverlay`), above the workspace and
 * onboarding.
 */
import { useEffect, useRef, type RefObject } from "react";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { useSelectionQuotePrefill } from "@/hooks/use-selection-quote-prefill";
import { isTauri } from "@/lib/is-tauri";
import { withMutationEmitSuppressed } from "@/lib/mirrored-store";
import type { WorkspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { useCanvasStore, type CanvasState } from "@/stores/canvas-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { useProjectStore } from "@/stores/project-store";
import { useStandaloneComposerDraft } from "@/stores/standalone-composer-draft-store";
import { useWorkspaceDoubleControlInput } from "./use-workspace-double-control-input";

export interface WorkspaceSummon {
  cursorInput: CanvasState["cursorInput"];
  value: string;
  setValue: ReturnType<typeof useStandaloneComposerDraft>["setValue"];
  close: () => void;
}

export function useWorkspaceSummon(
  rootRef: RefObject<HTMLElement | null>,
  adapter: WorkspaceViewAdapter,
): WorkspaceSummon {
  const currentProjectId = useProjectStore((s) => s.currentProject?.id);
  const currentSpaceId = useProjectStore((s) => s.currentSpace?.id);
  const desktopMode = useDesktopMode((s) => s.mode);
  const cursorInput = useCanvasStore((s) => s.cursorInput);
  const openCursorInput = useCanvasStore((s) => s.openCursorInput);
  const closeCursorInput = useCanvasStore((s) => s.closeCursorInput);
  const pendingCursorSummon = useCanvasStore((s) => s.pendingCursorSummon);
  const setPendingCursorSummon = useCanvasStore(
    (s) => s.setPendingCursorSummon,
  );
  const notePlacementActive = useCanvasStore((s) => s.notePlacementActive);
  const commentModeActive = useCanvasStore((s) => s.commentModeActive);
  const { draft, setValue } = useStandaloneComposerDraft();
  // Selection-aware summon: prepend the captured selection as a quote into
  // the workspace bar, once per summon (compact mode has its own bar with
  // the same hook in CompactCursorInputOverlay).
  useSelectionQuotePrefill(
    cursorInput?.selection,
    cursorInput?.summonId,
    setValue,
  );

  useWorkspaceDoubleControlInput({
    containerRef: rootRef,
    notePlacementActive,
    commentModeActive,
    toWorkspacePoint: adapter.toWorkspacePoint,
    openInput: openCursorInput,
    setInputValue: setValue,
  });

  const lastScopeRef = useRef<string | null>(null);
  useEffect(() => {
    const scope = `${currentProjectId ?? ""}:${currentSpaceId ?? ""}:${desktopMode}`;
    if (lastScopeRef.current === scope) return;
    lastScopeRef.current = scope;
    // Per-window interaction state reset — local-only. This fires on first
    // mount, and the owner's first mount can be triggered by a summon on a
    // different display; broadcasting `cursorInput: null` from here killed
    // that just-opened bar (first-summon flash-then-disappear bug).
    withMutationEmitSuppressed(() => {
      useCanvasStore.setState({
        isPanMode: false,
        commentModeActive: false,
        notePlacementActive: false,
        commentPopover: null,
        multiSelectContextMenu: null,
        cursorInput: null,
      });
    });
  }, [currentProjectId, currentSpaceId, desktopMode]);

  // Web summons open the workspace bar at the summon point.
  useEffect(() => {
    if (!pendingCursorSummon || isTauri()) return;
    if (
      pendingCursorSummon.displayId != null &&
      pendingCursorSummon.displayId !== getWindowDisplayId()
    ) {
      return;
    }
    const point = adapter.toWorkspacePoint({
      x: pendingCursorSummon.x,
      y: pendingCursorSummon.y,
    });
    openCursorInput({
      flowX: point.x,
      flowY: point.y,
      placement: pendingCursorSummon.placement ?? "cursor",
      summonId: pendingCursorSummon.summonId,
      displayId: pendingCursorSummon.displayId,
      selection: pendingCursorSummon.selection,
      agentKey: pendingCursorSummon.agentKey,
      submitMode: pendingCursorSummon.submitMode,
      submitEventName: pendingCursorSummon.submitEventName,
    });
    setPendingCursorSummon(null);
  }, [adapter, pendingCursorSummon, openCursorInput, setPendingCursorSummon]);

  return {
    cursorInput,
    value: draft.text,
    setValue,
    close: closeCursorInput,
  };
}
